import React, { useState, createContext, useEffect, useContext } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { RouteTypes } from '../constants/index';
import AuthService, { SystemDataType } from '../../api/services/auth';
import apiClient from '../../api/http-common';
import { configure } from 'axios-hooks';
import i18n from '~/components/i18n';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/nl';
import 'dayjs/locale/fr';
import LoaderMilkCan from '~/components/Loader';
import { Dimmer } from 'semantic-ui-react';
import { ThemeContext } from 'styled-components';
import { getRoute, casheLanguage, handleErrors, getCorrectLanguageCode } from '~/utils/helpers';
import SupportService from '~/api/services/support';
import { CLIMATE_CHECK_SLUG, FARMER_ROLE_SLUG } from '~/Constants';
import { UpdateThemeContext } from '~/Application'
import { AuthenticationResult } from '@azure/msal-browser';
import { loginRequest } from '~/authConfig';
import { useMsal } from '@azure/msal-react';

interface IAuthProviderProps {
  children: JSX.Element[] | JSX.Element;
}

interface IAuthContext {
  isAuthenticated: boolean;
  hasToken: string
  onLogout: any
  systemData?: SystemDataType
  isTfaRequired: boolean
  tfaType?: string
  loginQuery?: any
  fetchSystemData: any
  setIsTfaRequired?: any
  setForcePassword?: any
  setSystemData: any
  forcePassword?: boolean
  supportData: any
  setSupportData?: any
  // Azure AD specific methods and state
  loginWithAzure: () => Promise<void>
}

const AuthContext = createContext<IAuthContext>({
  isAuthenticated: false,
  hasToken: '',
  isTfaRequired: false,
  fetchSystemData: (route): void => {
    throw new Error('fetchSystemData function must be overridden');
  },
  onLogout: (): void => {
    throw new Error('onLogout function must be overridden');
  },
  setSystemData: (systemData: SystemDataType): void => {
    throw new Error('onLogout function must be overridden');
  },
  systemData: null,
  supportData: null,
  // Azure AD defaults
  loginWithAzure: async (): Promise<void> => {
    throw new Error('loginWithAzure function must be overridden');
  }
});

export const useAuth = () => {
  return React.useContext(AuthContext);
};

const AuthProvider: React.FunctionComponent<IAuthProviderProps> = (props) => {
  const { children } = props;
  const navigate = useNavigate();
  const systemDataContext = useContext(ThemeContext)
  const updateThemeContext = useContext(UpdateThemeContext);
  const navigationType = useNavigationType()
  const { instance } = useMsal();

  useEffect(() => {
    instance.initialize().catch(error => {
      console.error('MSAL initialization error:', error);
    });
  }, []);

  const [systemData, setSystemData] = useState(null)
  const [isBusy, setIsBusy] = useState(!!localStorage.getItem('token'))
  const [isTfaRequired, setIsTfaRequired] = useState(false)
  const [tfaType, setTfaType] = useState(null)
  const [forcePassword, setForcePassword] = useState(false)
  const [supportData, setSupportData] = useState(null)

  const loginWithAzure = async (): Promise<void> => {
    try {
      await instance.initialize();

      const response: AuthenticationResult = await instance.loginPopup(loginRequest);
      if (response?.account?.idToken) {
        const responseAzure = await AuthService.checkAzureToken({ idToken: response.account.idToken })
        if (responseAzure.data.token) {
          localStorage.setItem('token', responseAzure.data.token)
          fetchSystemData(true)
        }
      }
    } catch (error) {
      handleErrors(error)
    }
  };

  async function fetchSystemData(navigateFromLogin = false) {
    try {
      const { data: response } = await AuthService.getSystemData();
      const reload = navigationType === 'POP';
      const { logged, languages } = response;

      setSystemData({
        ...response,
        openedLoginPopups: !reload ? !logged.popUpsOnLogin : true
      });
      if (logged?.roleSlug === FARMER_ROLE_SLUG) {
        const { data: supportData } = await SupportService.getSupport();
        if (response.isOrganization) {
          const { data: favIcon } = await AuthService.getFarmerOrganizationHeadIcon()
          const { data: logoTitle } = await AuthService.getFarmerOrganizationLogo()
          const defaultThemeValues = {
            firstColor: systemDataContext?.firstColor,
            companyName: systemDataContext?.companyName,
            favIcon: systemDataContext?.favIcon,
            logoTitle: systemDataContext?.logoTitle
          }
          const loginLanguage = languages.find((language) => language.id === logged?.languageId);
          i18n.changeLanguage(loginLanguage.code);
          updateThemeContext?.setUpdatedTheme({
            ...systemDataContext,
            defaultThemeValues,
            firstColor: response?.firstColor,
            companyName: response?.companyName,
            favIcon,
            logoTitle,
            code: loginLanguage.code
          })
        }
        setSupportData(supportData);
      }

      const loginLanguage = languages.find((language) => language.id === logged?.languageId);

      if (loginLanguage) {
        const languageCode = getCorrectLanguageCode(loginLanguage.code)

        dayjs.locale(languageCode)
        if (loginLanguage.code !== localStorage.getItem('i18nextLng')) {
          i18n.changeLanguage(loginLanguage.code);
        }
        i18n.changeLanguage(loginLanguage.code);
      } else {
        casheLanguage(systemDataContext);
      }

      if (response.hideMenuAndHeader) {
        navigate(RouteTypes.CLIMATE_CHECK)
      }

      if (navigateFromLogin) {
        const farmerHasClimaCheck = Boolean(response.modules.find(module => module.slug === CLIMATE_CHECK_SLUG))
        const path = farmerHasClimaCheck ? RouteTypes.CLIMATE_CHECK : getRoute(logged?.roleSlug, response.operativeUsersCanSeeFarmersInfo);
        navigate(path);
      }
    } catch (error) {
      if (!localStorage.getItem('theme')) {
        localStorage.setItem('theme', JSON.stringify(systemDataContext));
      }
      casheLanguage(systemDataContext);
      localStorage.removeItem('token');
    }

    setIsBusy(false);
  }

  useEffect(() => {
    if (!systemData && localStorage.getItem('token')) {
      fetchSystemData()
    }
    apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        return Promise.reject(error);
      }
    );
    configure({ axios: apiClient });
  }, []);

  function tokenDidNotExpire() {
    //check if token did expire
    return true
  }

  const isSuccess = (!!localStorage.getItem('token') && systemData)

  async function handleLogut() {
    try {
      await AuthService.logout()
    } catch (error) {
      //do nothing
    }
    if (updateThemeContext) {
      updateThemeContext.setUpdatedTheme({
        ...systemDataContext,
        ...systemDataContext?.defaultThemeValues
      })
    }
    localStorage.removeItem('token');
    navigate(RouteTypes.LOGIN)
    setSystemData(null)
  }

  function setIsTfaRequiredFunc(isTfaRequired, tfaType) {
    setIsTfaRequired(isTfaRequired)
    if (tfaType) {
      setTfaType(tfaType)
    }
  }

  const value = {
    isTfaRequired: isTfaRequired,
    isAuthenticated: isSuccess,
    hasToken: localStorage.getItem('token'),
    onLogout: handleLogut,
    systemData: systemData,
    tfaType: tfaType,
    fetchSystemData: fetchSystemData,
    setIsTfaRequired: setIsTfaRequiredFunc,
    forcePassword: forcePassword,
    setForcePassword,
    setSystemData,
    supportData,
    setSupportData,
    // Azure AD methods and state
    loginWithAzure
  };

  if (isBusy && localStorage.getItem('token')) {
    return <Dimmer active={isBusy} inverted>
      <LoaderMilkCan isLoading={isBusy} />
    </Dimmer>
  }

  return <AuthContext.Provider value={value}>
    {children}
  </AuthContext.Provider>;
};

export default AuthProvider;
